import { Shield, Settings, Search, Lightbulb, Cloud, Code } from "lucide-react";
import { skillsQuery, competenciesQuery, siteSettingsQuery } from "@/lib/sanity";
import { useSanityQuery } from "@/hooks/use-sanity-query";
import type { Skill, Competency, SiteSettings } from "@/types/content";

export default function SkillsSection() {
  const { data: skills = [] } = useSanityQuery<Skill[]>(["sanity", "skills"], skillsQuery);

  const { data: competencies = [] } = useSanityQuery<Competency[]>(["sanity", "competencies"], competenciesQuery);

  const { data: settings } = useSanityQuery<SiteSettings>(["sanity", "siteSettings"], siteSettingsQuery);

  const learningSkills = skills.filter(skill => skill.category === "learning");

  return (
    <section id="skills" className="py-20 px-4 sm:px-6 lg:px-8">
      <div className="max-w-7xl mx-auto">
        <div className="text-center mb-16">
          <h2 className="text-4xl md:text-5xl font-bold mb-4 font-mono text-pink-400">
            {settings?.skillsHeading || "> cat skills.json"}
          </h2>
          <p className="text-xl text-gray-300">
            {settings?.skillsIntro || "Currently expanding knowledge in modern web technologies"}
          </p>
        </div>

        {/* Learning Skills */}
        {learningSkills.length > 0 && (
          <div className="grid md:grid-cols-3 gap-8 mb-12">
            {learningSkills.map((skill) => (
              <div key={skill.id} className="glass-card p-8 rounded-2xl text-center hover:scale-105 transition-transform">
                <div className="w-16 h-16 mx-auto mb-4 bg-gradient-to-br from-blue-400 to-blue-600 rounded-lg flex items-center justify-center">
                  {skill.iconUrl ? (
                    <img src={skill.iconUrl} alt={skill.name} className="w-8 h-8" />
                  ) : (
                    <span className="text-white font-bold text-2xl">{skill.name.charAt(0)}</span>
                  )}
                </div>
                <h3 className="text-xl font-bold mb-2 text-pink-400 font-mono">{skill.name}</h3>
                <p className="text-gray-300 text-sm mb-4">{skill.description}</p>
                <div className="bg-dark-bg rounded-full h-2 mb-2">
                  <div 
                    className="bg-gradient-to-r from-pink-500 to-pink-400 h-2 rounded-full transition-all duration-300"
                    style={{ width: `${skill.level ?? 0}%` }}
                  ></div>
                </div>
                <span className="text-xs font-mono text-gray-400">Learning Progress: {skill.level ?? 0}%</span>
              </div>
            ))}
          </div>
        )}

        {/* Core Competencies */}
        {competencies.length > 0 && (
          <div className="glass-card p-8 rounded-2xl">
            <h3 className="text-2xl font-bold mb-6 text-pink-400 font-mono text-center">
              {settings?.competenciesHeading || "Core Competencies"}
            </h3>
            <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
              {competencies.map((competency) => {
                // Map competency titles to icons
                const getIcon = (iconName: string) => {
                  switch (iconName) {
                    case 'shield':
                      return <Shield className="w-8 h-8 text-pink-400" />;
                    case 'settings':
                      return <Settings className="w-8 h-8 text-pink-400" />;
                    case 'search':
                      return <Search className="w-8 h-8 text-pink-400" />;
                    case 'lightbulb':
                      return <Lightbulb className="w-8 h-8 text-pink-400" />;
                    case 'cloud':
                      return <Cloud className="w-8 h-8 text-pink-400" />;
                    case 'code':
                      return <Code className="w-8 h-8 text-pink-400" />;
                    default:
                      return <Shield className="w-8 h-8 text-pink-400" />;
                  }
                };
                
                return (
                  <div key={competency.id} className="text-center">
                    <div className="flex justify-center mb-3">
                  {getIcon(competency.icon || "shield")}
                </div>
                    <h4 className="font-mono text-pink-400 mb-2">{competency.title}</h4>
                    <p className="text-sm text-gray-300">{competency.description}</p>
                  </div>
                );
              })}
            </div>
          </div>
        )}

        {skills.length === 0 && competencies.length === 0 && (
          <div className="text-center py-12">
            <p className="text-gray-400 font-mono">No skills found yet.</p>
          </div>
        )}
      </div>
    </section>
  );
}
